import {
  Anchor,
  Box,
  Group,
  PasswordInput,
  PasswordInputProps,
  Popover,
  Progress,
  Text,
  useMantineTheme,
} from '@mantine/core'
import {
  CSSProperties,
  MouseEventHandler,
  ReactNode,
  Ref,
  useState,
} from 'react'
import {
  Controller,
  FieldError,
  RefCallBack,
  useFormContext,
} from 'react-hook-form'
import { IconType } from 'react-icons/lib'
import { FiLock } from 'react-icons/fi'
import { HiOutlineCheck } from 'react-icons/hi'
import { ImCross } from 'react-icons/im'
import { getPasswordStrength, RequirementsType } from 'shared/utils'
import { Link, To, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface InputPasswordProps {
  name: string
  label?: string | ReactNode
  icon?: IconType
  description?: string
  placeholder?: string
  requirements?: RequirementsType[]
  required?: boolean
  newPassword?: boolean
  autoComplete?: PasswordInputProps['autoComplete']
  forgotPasswordLink?: To
  forgotPasswordState?: string
}

interface InputPasswordPureProps
  extends Omit<PasswordInputProps, 'icon' | 'error' | 'ref'> {
  label?: string | ReactNode
  icon?: IconType
  description?: string
  placeholder?: string
  error?: FieldError
  innerRef: RefCallBack
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  value: string
  requirements?: RequirementsType[]
  forgotPasswordLink?: To
  forgotPasswordState?: string
}

export const InputPassword = ({
  name,
  icon,
  requirements,
  newPassword,
  ...props
}: InputPasswordProps) => {
  const { control } = useFormContext()
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { ref, ...field }, fieldState }) => {
        const { error } = fieldState
        if (requirements)
          return (
            <InputPasswordPureWithRequirements
              autoComplete={newPassword ? 'new-password' : undefined}
              {...field}
              {...props}
              innerRef={ref}
              error={error}
              icon={icon}
              requirements={requirements}
            />
          )
        else
          return (
            <InputPasswordPure
              autoComplete={newPassword ? 'new-password' : undefined}
              {...field}
              {...props}
              innerRef={ref}
              error={error}
              icon={icon}
            />
          )
      }}
    />
  )
}

export const InputPasswordPureWithRequirements = ({
  icon: Icon,
  error,
  innerRef,
  value = '',
  onChange,
  requirements = [],
  ...props
}: InputPasswordPureProps) => {
  const [popoverOpened, setPopoverOpened] = useState(false)
  const checks = requirements.map(
    (requirement: RequirementsType, index: number) => (
      <PasswordRequirement
        key={index}
        label={requirement.label}
        meets={requirement.re.test(value)}
      />
    ),
  )

  const strength = getPasswordStrength(requirements, value)
  const color = strength === 100 ? 'teal' : strength > 50 ? 'yellow' : 'red'

  return (
    <Popover
      opened={popoverOpened}
      position="bottom-end"
      withArrow
      trapFocus={false}
      transitionProps={{
        transition: 'pop-top-left',
      }}
    >
      <Popover.Target>
        <PasswordInput
          ref={innerRef as Ref<HTMLInputElement>}
          leftSection={
            Icon ? <Icon size={'1.2em'} /> : <FiLock size={'1.2em'} />
          }
          error={error?.message}
          onChange={onChange}
          value={value ?? ''}
          onFocusCapture={() => setPopoverOpened(true)}
          onBlurCapture={() => setPopoverOpened(false)}
          {...props}
        />
      </Popover.Target>
      <Popover.Dropdown>
        <Progress
          color={color}
          value={strength}
          size={5}
          style={{ marginBottom: 10 }}
        />
        {checks}
      </Popover.Dropdown>
    </Popover>
  )
}

export const InputPasswordPure = ({
  icon: Icon,
  error,
  innerRef,
  value = '',
  onChange,
  className,
  style,
  label,
  forgotPasswordLink,
  forgotPasswordState,
  ...props
}: InputPasswordPureProps) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const forgotPasswordStyle = {
    paddingTop: 2,
    color:
      theme.colors[theme.primaryColor][theme.colorScheme === 'dark' ? 4 : 7],
    fontWeight: 500,
    fontSize: theme.fontSizes.xs,
  } as CSSProperties

  const handleForgotPassword: MouseEventHandler<HTMLAnchorElement> = e => {
    if (!forgotPasswordLink || !forgotPasswordState) return
    e.preventDefault()
    navigate(forgotPasswordLink, {
      state: forgotPasswordState,
    })
  }

  return (
    <div className={className} style={style as CSSProperties}>
      <Group justify="space-between" mb={5}>
        <Text component="label" htmlFor={props.name} size="sm" fw={500}>
          {label}
        </Text>

        {forgotPasswordLink && (
          <Anchor
            data-testid={forgotPasswordLink.toString().substring(1)}
            component={Link}
            to={forgotPasswordLink}
            onClick={handleForgotPassword}
            style={forgotPasswordStyle}
          >
            {t('user.forgotPasswordCta')}
          </Anchor>
        )}
      </Group>
      <PasswordInput
        ref={innerRef as Ref<HTMLInputElement>}
        leftSection={Icon ? <Icon size={'1.2em'} /> : <FiLock size={'1.2em'} />}
        error={error?.message}
        onChange={onChange}
        value={value ?? ''}
        {...props}
      />
    </div>
  )
}

const PasswordRequirement = ({
  meets,
  label,
}: {
  meets: boolean
  label: string
}) => {
  return (
    <Text
      color={meets ? 'teal' : 'red'}
      style={{ display: 'flex', alignItems: 'center' }}
      mt={7}
      size="sm"
    >
      {meets ? <HiOutlineCheck /> : <ImCross />} <Box ml={10}>{label}</Box>
    </Text>
  )
}
